import React from 'react';

export interface PartenaireItem {
  id: number;
  code: string;
  nom: string;
  description: string | null;
  discord_link: string;
  color: string;
  icon: string | null;
  ordre: number;
  actif: number;
  created_at?: string;
}

const PARTENAIRE_COLORS = ['blue', 'red', 'green', 'purple', 'yellow', 'orange'] as const;

interface PartenairesTabProps {
  t: (key: string) => string;
  loading: boolean;
  partenairesConfig: PartenaireItem[];
  partnerForm: {
    code: string;
    nom: string;
    description: string;
    discord_link: string;
    color: typeof PARTENAIRE_COLORS[number];
    icon: string;
    ordre: number;
    actif: number;
  };
  setPartnerForm: React.Dispatch<React.SetStateAction<{
    code: string;
    nom: string;
    description: string;
    discord_link: string;
    color: typeof PARTENAIRE_COLORS[number];
    icon: string;
    ordre: number;
    actif: number;
  }>>;
  editingPartnerId: number | null;
  savingPartner: boolean;
  handleSavePartner: () => void;
  handleCancelEditPartner: () => void;
  handleEditPartner: (p: PartenaireItem) => void;
  handleDeletePartner: (id: number) => void;
}

const PartenairesTab: React.FC<PartenairesTabProps> = ({
  t,
  loading,
  partenairesConfig,
  partnerForm,
  setPartnerForm,
  editingPartnerId,
  savingPartner,
  handleSavePartner,
  handleCancelEditPartner,
  handleEditPartner,
  handleDeletePartner,
}) => (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-400">
                      {t('partenaires.description')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.codeLabel')}</label>
                        <input
                          type="text"
                          value={partnerForm.code}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, code: e.target.value }))}
                          placeholder={t('partenaires.codePlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                          disabled={!!editingPartnerId}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.nomLabel')}</label>
                        <input
                          type="text"
                          value={partnerForm.nom}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, nom: e.target.value }))}
                          placeholder={t('partenaires.nomPlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.descriptionLabel')}</label>
                        <input
                          type="text"
                          value={partnerForm.description}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, description: e.target.value }))}
                          placeholder={t('partenaires.descriptionPlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.discordLabel')}</label>
                        <input
                          type="url"
                          value={partnerForm.discord_link}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, discord_link: e.target.value }))}
                          placeholder={t('partenaires.discordPlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.couleurLabel')}</label>
                        <select
                          value={partnerForm.color}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, color: e.target.value as typeof PARTENAIRE_COLORS[number] }))}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        >
                          {PARTENAIRE_COLORS.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-400">{t('partenaires.discordLogoNote')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('partenaires.ordreLabel')}</label>
                        <input
                          type="number"
                          min={0}
                          value={partnerForm.ordre}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, ordre: parseInt(e.target.value, 10) || 0 }))}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="partner-actif"
                          checked={!!partnerForm.actif}
                          onChange={(e) => setPartnerForm((f) => ({ ...f, actif: e.target.checked ? 1 : 0 }))}
                          className="rounded border-gray-600 bg-gray-800 text-primary-500"
                        />
                        <label htmlFor="partner-actif" className="text-sm text-gray-300">{t('partenaires.actifLabel')}</label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSavePartner}
                        disabled={savingPartner}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium disabled:opacity-60"
                      >
                        {savingPartner ? t('partenaires.saving') : editingPartnerId ? t('partenaires.update') : t('partenaires.add')}
                      </button>
                      {editingPartnerId && (
                        <button
                          type="button"
                          onClick={handleCancelEditPartner}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium"
                        >
                          {t('partenaires.cancel')}
                        </button>
                      )}
                    </div>
                    <div className="border-t border-gray-700 pt-4">
                      <h2 className="text-lg font-semibold text-white mb-3">{t('partenaires.listTitle')}</h2>
                      {loading ? (
                        <p className="text-sm text-gray-500">{t('partenaires.loading')}</p>
                      ) : partenairesConfig.length === 0 ? (
                        <p className="text-sm text-gray-500">{t('partenaires.empty')}</p>
                      ) : (
                        <ul className="divide-y divide-gray-800">
                          {partenairesConfig.map((p) => (
                            <li key={p.id} className="py-2 flex items-center justify-between gap-4">
                              <span className={`flex-1 ${p.actif ? 'text-gray-200' : 'text-gray-500'}`}>
                                {p.nom} <span className="text-gray-500">({p.code})</span>
                                {!p.actif && <span className="ml-2 text-xs text-amber-400">{t('partenaires.inactif')}</span>}
                              </span>
                              <span className="text-xs text-gray-500">{t('partenaires.ordre')}: {p.ordre}</span>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditPartner(p)}
                                  className="px-2 py-1 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded"
                                >
                                  {t('partenaires.modify')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeletePartner(p.id)}
                                  className="px-2 py-1 text-sm bg-red-600/80 hover:bg-red-600 text-white rounded"
                                >
                                  {t('partenaires.delete')}
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
);

export default PartenairesTab;
